import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import MarketTotalRevenueContributionBreakdownTableSources from 'lumen/components/market/total-revenue/contribution-breakdown/table-sources';
import MarketTotalRevenueContributionBreakdownTrendChart from 'lumen/components/market/total-revenue/contribution-breakdown/trend-chart';
import MarketTotalRevenueVarianceDisplay from 'lumen/components/market/total-revenue/variance-display';
import type { TotalRevenueBreakdownItem } from 'lumen/models';

interface MarketTotalRevenueContributionBreakdownItemSignature {
  Args: {
    department: TotalRevenueBreakdownItem;
    exportSourceTransactions: (source: TotalRevenueBreakdownItem) => void;
    isSelected?: boolean;
    percentageOfNonRoomRevenue: number | null;
    selectedReferenceType: string;
    updateSelectedDepartment: (departmentId: string) => void;
  };
}

export default class MarketTotalRevenueContributionBreakdownItem extends Component<MarketTotalRevenueContributionBreakdownItemSignature> {
  @service declare totalRevenue: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedReference() {
    return this.args.department.referenceValues.find(
      (reference) =>
        reference.referenceType === this.args.selectedReferenceType,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sources() {
    return [...(this.args.department.sources ?? [])];
  }

  @action updateDepartment(): void {
    const { department, updateSelectedDepartment } = this.args;

    updateSelectedDepartment(department.id!);
  }

  <template>
    {{! template-lint-disable no-passed-in-event-handlers }}
    <PrismCard data-test-id="contribution-breakdown-item">
      <:header as |Header|>
        <Header.title data-test-id="contribution-breakdown-item-title">
          {{@department.translatedCategory}}
        </Header.title>
      </:header>

      <:headerAfter>
        {{#unless @isSelected}}
          <button
            class="button--unstyled prism-link prism-link--secondary prism-link--small prism-link--standalone"
            type="button"
            {{on "click" this.updateDepartment}}
          >
            {{t "total_revenue.see_sources"}}
            <PrismIcon @color="inherit" @name="arrow-right" @size="small" />
          </button>
        {{/unless}}
      </:headerAfter>

      <:content>
        <div class="flex flex-col {{if @isSelected 'gap-800' 'gap-400'}}">
          <div
            class="flex flex-row
              {{if @isSelected 'gap-800' 'flex justify-between'}}"
          >
            <div>
              <p class="heading-1">{{@department.formattedValue}}</p>
              <p class="text-200--regular">
                {{t
                  "total_revenue.percentage_of_non_room_revenue"
                  value=(this.totalRevenue.formatPercentage
                    @percentageOfNonRoomRevenue
                  )
                }}
              </p>
            </div>
            {{#if this.selectedReference}}
              <MarketTotalRevenueVarianceDisplay
                @change={{this.selectedReference.change}}
                @formattedChange={{this.selectedReference.formattedChange}}
                @formattedRelativeChange={{this.selectedReference.formattedRelativeChange}}
                @referencePeriod={{this.selectedReference.translatedReferenceType}}
                @relativeChange={{this.selectedReference.relativeChange}}
                class="text-200--medium"
              />
            {{/if}}
          </div>

          <MarketTotalRevenueContributionBreakdownTrendChart
            @department={{@department}}
            @isSelected={{@isSelected}}
            @selectedReferenceType={{@selectedReferenceType}}
            @trendItems={{@department.sortedTrendItems}}
          />

          {{#if @isSelected}}
            <div class="flex flex-col gap-400">
              <span class="heading-3">{{t "total_revenue.sources"}}</span>
              <MarketTotalRevenueContributionBreakdownTableSources
                @exportSourceTransactions={{@exportSourceTransactions}}
                @selectedReferenceType={{@selectedReferenceType}}
                @sources={{this.sources}}
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
